import React, { useState, useEffect } from "react";
import { auth, db, ref, onValue, get } from "./firebase";
import { useAuth } from "./AuthContext";
import { FiBook, FiChevronDown, FiChevronUp, FiCalendar, FiCheckCircle, FiAlertCircle, FiClock } from "react-icons/fi";

const HomeworkPage = () => {
  const [homeworks, setHomeworks] = useState([]);
  const [username, setUsername] = useState("Loading...");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const { enrolledCourse } = useAuth();

  const toggleDescription = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError("User not authenticated. Please log in.");
          setLoading(false);
          return;
        }

        const email = currentUser.email;
        let foundUser = null;
        let userCourse = null;

        // First check if user is admin/assistant in users collection
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);

        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          foundUser = Object.values(usersData).find(
            (u) => u.email === email
          );
          if (foundUser) {
            setUsername(foundUser.name);
            userCourse = foundUser.enrolledCourse;
          }
        }

        // If not found in users collection, search in courses/students
        if (!foundUser || !userCourse) {
          const coursesRef = ref(db, "courses");
          const coursesSnapshot = await get(coursesRef);

          if (coursesSnapshot.exists()) {
            const courses = coursesSnapshot.val();

            // Search through each course's students
            for (const [courseId, courseData] of Object.entries(courses)) {
              if (courseData.students) {
                const student = Object.values(courseData.students).find(
                  (s) => s.email === email
                );
                if (student) {
                  foundUser = student;
                  setUsername(student.name);
                  userCourse = courseId;
                  break;
                }
              }
            }
          }
        }

        if (!foundUser) {
          setError("User data not found. Please complete your profile.");
          setLoading(false);
          return;
        }

        if (!userCourse) {
          setError("No enrolled course found. Please enroll in a course.");
          setLoading(false);
          return;
        }

        // Fetch homeworks for the enrolled course
        const homeworksRef = ref(db, `courses/${userCourse}/homeworks`);
        const unsubscribeHomeworks = onValue(homeworksRef, (homeworkSnapshot) => {
          if (homeworkSnapshot.exists()) {
            const currentDate = new Date();
            let allHomeworks = Object.entries(homeworkSnapshot.val())
              .map(([id, hw]) => {
                const dueDate = new Date(hw.dueDate);
                const isDue = currentDate >= dueDate;
                const userStatus = foundUser.homeworkStatus?.[id];

                return {
                  id,
                  ...hw,
                  status: !isDue ? "not due yet" :
                    userStatus === "done" ? "done" :
                      userStatus === "incomplete" ? "incomplete" : "missing",
                  dueDateObj: dueDate,
                  published: hw.published !== false // Default to true
                };
              })
              .filter(hw => hw.published) // Only show published homeworks
              .sort((a, b) => b.dueDateObj - a.dueDateObj); // Sort by due date, newest first

            setHomeworks(allHomeworks);
          } else {
            setHomeworks([]);
            setError("No homeworks found for the enrolled course.");
          }
          setLoading(false);
        }, (error) => {
          console.error("Error fetching homeworks:", error);
          setError("Failed to fetch homeworks. Please try again later.");
          setLoading(false);
        });

        return () => unsubscribeHomeworks();
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to fetch user data. Please try again later.");
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "done":
        return "bg-green-500/20 text-green-400";
      case "incomplete":
        return "bg-yellow-500/20 text-yellow-400";
      case "missing":
        return "bg-red-500/20 text-red-400";
      default:
        return "bg-teal-500/20 text-teal-400";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "done":
        return <FiCheckCircle className="text-green-400" />;
      case "incomplete":
        return <FiAlertCircle className="text-yellow-400" />;
      case "missing":
        return <FiAlertCircle className="text-red-400" />;
      default:
        return <FiClock className="text-teal-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="bg-red-500/10 p-6 rounded-xl w-full max-w-md text-center border border-red-500/20">
          <div className="text-red-400 font-medium text-lg mb-2">Error</div>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6 w-full">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-teal-600 to-sky-600 rounded-2xl p-6 md:p-8 shadow-xl mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Homework</h1>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-teal-100 text-sm md:text-base">Total Assignments:</span>
            <span className="text-white font-bold text-lg md:text-xl">{homeworks.length}</span>
          </div>
        </div>
      </div>

      {/* Homework List */}
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        {homeworks.length > 0 ? (
          homeworks.map((hw) => (
            <div
              key={hw.id}
              className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden hover:border-teal-500/30 transition-all duration-300"
            >
              <div className="p-6">
                <div
                  className="flex items-start justify-between cursor-pointer"
                  onClick={() => toggleDescription(hw.id)}
                >
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center text-teal-400 flex-shrink-0">
                      <FiBook className="text-xl" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-white mb-1 break-words">{hw.title}</h3>
                      <div className="flex items-center space-x-3 text-sm text-gray-400 flex-wrap">
                        <div className="flex items-center space-x-1">
                          <FiCalendar className="text-gray-400" />
                          <span>Due: {hw.dueDateObj.toLocaleDateString()}</span>
                        </div>
                        <div className={`flex items-center space-x-1 px-3 py-1 rounded-full ${getStatusColor(hw.status)}`}>
                          {getStatusIcon(hw.status)}
                          <span>{hw.status.charAt(0).toUpperCase() + hw.status.slice(1)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    className="text-gray-400 hover:text-white transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDescription(hw.id);
                    }}
                  >
                    {expandedId === hw.id ? (
                      <FiChevronUp className="text-xl" />
                    ) : (
                      <FiChevronDown className="text-xl" />
                    )}
                  </button>
                </div>
              </div>

              {expandedId === hw.id && (
                <div className="px-6 pb-6">
                  <div className="bg-gray-700/30 rounded-lg p-4">
                    <p className="text-gray-300 break-words whitespace-pre-wrap">{hw.description}</p>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-8 text-center">
            <FiBook className="mx-auto text-4xl text-gray-400 mb-4" />
            <p className="text-gray-400 text-lg">No homework assignments found</p>
            <p className="text-gray-500 mt-2">Please check back later for updates</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeworkPage;